import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { EASE } from "./motion";
import reconSlots from "../../content/recon-slots.json";

const PROJECT_TYPE_LABELS = {
  new: "New site",
  redesign: "Redesign",
  maintenance: "Ongoing maintenance",
};
const TIMELINE_LABELS = {
  asap: "ASAP",
  soon: "1–2 months",
  exploring: "Just exploring",
};

const RECON_EMAIL = "jamie.ziglerx2@gmail.com";

function buildMailto(slot, quizAnswers) {
  const subject = `Recon call — ${quizAnswers.tier} (${slot.day} ${slot.date})`;
  const body = [
    `Requested slot: ${slot.day}, ${slot.date} at ${slot.time}`,
    "",
    `Project type: ${PROJECT_TYPE_LABELS[quizAnswers.projectType] || quizAnswers.projectType}`,
    `Timeline: ${TIMELINE_LABELS[quizAnswers.timeline] || quizAnswers.timeline}`,
    `Recommended tier: ${quizAnswers.tier} (${quizAnswers.price})`,
  ].join("\n");
  return `mailto:${RECON_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function ReconBooking({ quizAnswers, onBack }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const slots = reconSlots.slots || [];

  const pickSlot = (slot) => {
    setSelectedSlot(slot);
    window.location.href = buildMailto(slot, quizAnswers);
  };

  if (selectedSlot) {
    return (
      <div data-testid="recon-booking-confirmation" className="text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-pine text-paper">
          <Check className="h-6 w-6" />
        </span>
        <h3 className="mt-5 font-serif text-3xl text-ink">Recon call requested</h3>
        <p className="mt-3 max-w-sm mx-auto text-sm leading-relaxed text-ink-dim">
          Your email client should have opened with the details filled in —{" "}
          <span className="font-mono text-ink">
            {selectedSlot.day}, {selectedSlot.date} at {selectedSlot.time}
          </span>
          . Hit send and I'll confirm the slot directly.
        </p>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim">
          Recommended tier: <span className="text-pine">{quizAnswers.tier}</span>
        </p>
      </div>
    );
  }

  return (
    <div data-testid="recon-booking">
      <button
        onClick={onBack}
        data-testid="recon-booking-back"
        className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-dim transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <h3 className="mt-6 font-serif text-2xl text-ink">Book your recon call</h3>
      <p className="mt-2 text-sm leading-relaxed text-ink-dim">
        15 minutes to confirm scope — I'll already know you're looking at{" "}
        <span className="text-pine">{quizAnswers.tier}</span>.
      </p>

      <div className="mt-6 space-y-2.5">
        <AnimatePresence>
          {slots.length === 0 ? (
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-ink-dim">
              No open slots this month — text (719) 440-9494 directly.
            </p>
          ) : (
            slots.map((slot, i) => (
              <motion.button
                key={`${slot.date}-${slot.time}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05, ease: EASE }}
                onClick={() => pickSlot(slot)}
                data-testid={`recon-slot-${slot.date}`}
                className="flex w-full items-center justify-between rounded-lg border border-hairline bg-panel/60 px-5 py-4 text-left transition-colors duration-200 hover:border-pine/50"
              >
                <span className="font-serif text-lg text-ink">
                  {slot.day}, {slot.date}
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.16em] text-ink-dim">
                  {slot.time}
                </span>
              </motion.button>
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
